/**
 * PricingPage — Credits-only pricing display
 *
 * Public-facing pricing page showing the three tiers:
 *   - Pro ($99/mo — 5,000 credits, 3 users)
 *   - Business ($299/mo — 20,000 credits, 25 users, highlighted)
 *   - Enterprise ($999/mo — 50,000 credits, unlimited users)
 *
 * This is designed to be embedded in finance.rule42.ai/pricing or served standalone.
 *
 * @module packages/billing-admin/src/pages/PricingPage
 * @since 2026-06-07
 */
import React from 'react';

interface PricingTier {
  name: string;
  price: number;
  credits: string;
  users: string;
  highlighted: boolean;
  features: string[];
  cta: string;
}

const TIERS: PricingTier[] = [
  {
    name: 'Pro',
    price: 99,
    credits: '5,000',
    users: '3 users',
    highlighted: false,
    features: [
      '5,000 AI credits/month',
      'Up to 3 team members',
      'Full ATP execution',
      'Decomposition engine',
      'File indexing',
      'Email support',
    ],
    cta: 'Start with Pro',
  },
  {
    name: 'Business',
    price: 299,
    credits: '20,000',
    users: '25 users',
    highlighted: true,
    features: [
      '20,000 AI credits/month',
      'Up to 25 team members',
      'Full ATP execution',
      'Decomposition engine',
      'Research agent',
      'Priority support',
      'User budgets',
      'Usage analytics',
    ],
    cta: 'Start with Business',
  },
  {
    name: 'Enterprise',
    price: 999,
    credits: '50,000',
    users: 'Unlimited users',
    highlighted: false,
    features: [
      '50,000 AI credits/month',
      'Unlimited team members',
      'Full ATP execution',
      'Decomposition engine',
      'Research agent',
      'Dedicated support',
      'User budgets',
      'Usage analytics',
      'SSO/SAML',
      'Custom integrations',
      'SLA guarantees',
    ],
    cta: 'Contact Sales',
  },
];

const CREDIT_COSTS = [
  { action: 'ATP message (per 1K tokens)', credits: 1 },
  { action: 'Decomposition', credits: 5 },
  { action: 'File indexing (per file)', credits: 1 },
  { action: 'Research query', credits: 3 },
  { action: 'Agent session (per minute)', credits: 2 },
  { action: 'ATP completion', credits: 2 },
  { action: 'UoW completion', credits: 1 },
];

function TierCard({ tier }: { tier: PricingTier }): React.JSX.Element {
  return (
    <div
      className={`relative flex flex-col rounded-3xl border p-8 shadow-xl ${
        tier.highlighted
          ? 'border-cyan-400 bg-gradient-to-b from-slate-900 to-slate-800 text-white ring-2 ring-cyan-400/50'
          : 'border-slate-200 bg-white text-slate-900'
      }`}
    >
      {tier.highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-500 px-4 py-1 text-xs font-bold uppercase tracking-widest text-white shadow-lg">
          Most Popular
        </span>
      )}
      <h3 className={`text-lg font-semibold ${tier.highlighted ? 'text-cyan-300' : 'text-cyan-700'}`}>
        {tier.name}
      </h3>
      <div className="mt-4">
        <span className="text-4xl font-bold">${tier.price}</span>
        <span className={`text-sm ${tier.highlighted ? 'text-slate-400' : 'text-slate-500'}`}>/month</span>
      </div>
      <p className={`mt-2 text-sm font-medium ${tier.highlighted ? 'text-cyan-200' : 'text-slate-600'}`}>
        {tier.credits} credits &middot; {tier.users}
      </p>
      <ul className="mt-6 flex-1 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <svg className={`mt-0.5 h-4 w-4 flex-shrink-0 ${tier.highlighted ? 'text-cyan-400' : 'text-cyan-600'}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className={tier.highlighted ? 'text-slate-300' : 'text-slate-600'}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        className={`mt-8 w-full rounded-xl py-3 text-sm font-semibold transition ${
          tier.highlighted
            ? 'bg-cyan-500 text-white hover:bg-cyan-400 shadow-lg shadow-cyan-500/25'
            : 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50'
        }`}
      >
        {tier.cta}
      </button>
    </div>
  );
}

export default function PricingPage(): React.JSX.Element {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-4 py-16">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            Simple, credits-based pricing
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Pay for what you use. Every AI action consumes credits from your monthly allowance.
            No hidden fees. No per-seat surprises.
          </p>
        </div>

        {/* Tier cards */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
          {TIERS.map((tier) => (
            <TierCard key={tier.name} tier={tier} />
          ))}
        </div>

        {/* Credit cost table */}
        <div className="mt-24">
          <h2 className="text-center text-2xl font-semibold text-slate-900">
            What costs credits?
          </h2>
          <p className="mt-2 text-center text-sm text-slate-500">
            Every AI-powered action uses credits proportional to its computational cost
          </p>
          <div className="mx-auto mt-8 max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Action</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Credits</th>
                </tr>
              </thead>
              <tbody>
                {CREDIT_COSTS.map((row) => (
                  <tr key={row.action} className="border-t border-slate-50">
                    <td className="px-6 py-3 text-sm text-slate-700">{row.action}</td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-slate-900">{row.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ-ish note */}
        <div className="mx-auto mt-16 max-w-2xl rounded-2xl border border-cyan-100 bg-cyan-50/50 p-8 text-center">
          <h3 className="text-lg font-semibold text-slate-900">Need more credits?</h3>
          <p className="mt-2 text-sm text-slate-600">
            Credits can be purchased on-demand at any time. Unused credits roll over to the next month.
            Enterprise customers can configure custom credit pools and user budgets.
          </p>
        </div>
      </div>
    </div>
  );
}
